import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { Callout } from '@client/components/_ui/feedback/Callout';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { Progress } from '@client/components/_ui/feedback/Progress';
import { Space } from '@client/components/_ui/layout/Space';
import { isWhereClauseEmpty } from '@client/components/filters/core/where-builder';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { useDeleteConfirmation } from '@client/hooks/ui/useDeleteConfirmation';
import { type RouterInputs, trpc } from '@client/trpc/client';
import { DEEP_LIST_OFFSET_LIMIT, DEFAULT_PAGE_SIZE } from '@shared/constants';
import { FILE_TRASH_BULK_LIMIT } from '@shared/constants/system/file';
import { chunk } from '@shared/utils/core/array';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Trash2 } from 'lucide-react';
import { type Key, useEffect, useRef, useState } from 'react';
import { type TrashRow, useTrashTableColumns } from './columns';
import { TrashFilters } from './TrashFilters';

export const Route = createFileRoute('/console/$orgId/configuration/org-settings/trash/files')({
    staticData: {
        metadata: { title: 'Files' },
    },
    component: OrgTrash,
});

type QueryVariables = RouterInputs['file']['listDeletedFiles'];
type Where = QueryVariables['where'];

/** A confirmed purge in flight. Empty trash learns its `total` from the first batch's `remaining`. */
type PurgeProgress = { deleted: number; total: number | null };

const filesCount = (count: number) => `${count} file${count === 1 ? '' : 's'}`;

function OrgTrash() {
    const [queryVariables, setQueryVariables] = usePathState<QueryVariables>('queryVariables', {
        orderBy: { deletedAt: 'desc' },
        limit: DEFAULT_PAGE_SIZE,
    } as QueryVariables);

    const [filterWhere, setFilterWhere] = useState<Where>({});
    const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);
    // Ids hidden optimistically while a restore / permanent-delete is in flight,
    // so the row disappears immediately instead of lingering until the request
    // (which includes the storage delete) and refetch complete.
    const [removedIds, setRemovedIds] = useState<ReadonlySet<string>>(() => new Set());

    const { data, isFetching, refetch } = trpc.file.listDeletedFiles.useQuery({
        ...queryVariables,
        where: filterWhere,
    });

    // Clear optimistic removals once a fresh server result arrives (the affected
    // rows are already absent from it on success).
    useEffect(() => {
        setRemovedIds(new Set());
    }, [data]);

    const hideRows = (ids: string[]) => setRemovedIds((prev) => new Set([...prev, ...ids]));
    const unhideRows = (ids: string[]) =>
        setRemovedIds((prev) => {
            const next = new Set(prev);
            for (const id of ids) next.delete(id);
            return next;
        });

    const rows = (data?.rows ?? []).filter((row) => !removedIds.has(row.id));
    const total = data?.total ?? 0;
    // Reconcile preserved selections with refreshed capabilities before any bulk action.
    const readOnlyIds = new Set((data?.rows ?? []).filter((row) => !row.canManageTrash).map((row) => row.id));
    const writableSelectedRowKeys = selectedRowKeys.filter((key) => !readOnlyIds.has(String(key)));

    useEffect(() => {
        setSelectedRowKeys((keys) =>
            keys.filter((key) => !data?.rows.some((row) => row.id === key && !row.canManageTrash)),
        );
    }, [data]);

    // A restore or purge can span many requests. Each one carries the organisation captured
    // when the action started, so an organisation switch mid-loop cannot retarget it.
    const orgId = useOrgId();
    const { trpcClient } = useTrpc();
    const { mutateAsync: restoreFiles } = useMutation({
        mutationFn: ({ ids, orgId }: { ids: string[]; orgId: bigint }) =>
            trpcClient.file.restoreFiles.mutate({ ids }, { context: { orgIdOverride: orgId } }),
    });
    const { mutateAsync: permanentlyDeleteFiles } = useMutation({
        mutationFn: ({ ids, orgId }: { ids: string[]; orgId: bigint }) =>
            trpcClient.file.permanentlyDeleteFile.mutate({ ids }, { context: { orgIdOverride: orgId } }),
    });
    const { mutateAsync: emptyTrashBatch } = useMutation({
        mutationFn: ({ orgId, cutoff }: { orgId: bigint; cutoff?: Date }) =>
            trpcClient.file.emptyTrash.mutate({ cutoff }, { context: { orgIdOverride: orgId } }),
    });

    // A restore or purge spans many requests (one per batch), so every trash action stays
    // disabled until it finishes: Empty trash during a restore would purge the batches it has
    // not sent yet, a second purge would race the first over the same files, and a restore
    // could target files a purge is deleting. The ref closes the gap before the disabled
    // state renders.
    const [isRestoring, setIsRestoring] = useState(false);
    const [purgeProgress, setPurgeProgress] = useState<PurgeProgress | null>(null);
    const busyRef = useRef(false);
    const isBusy = isRestoring || purgeProgress !== null;

    // The selection persists across pages, so it can exceed the per-request cap:
    // send it in chunks. Chunks that committed before a failure stay restored.
    const handleRestore = async (ids: string[]) => {
        if (busyRef.current) return;
        busyRef.current = true;
        const restoreOrgId = orgId;
        hideRows(ids);
        setIsRestoring(true);
        const restoredIds: string[] = [];
        try {
            for (const batch of chunk(ids, FILE_TRASH_BULK_LIMIT)) {
                await restoreFiles({ ids: batch, orgId: restoreOrgId });
                restoredIds.push(...batch);
            }
            message.success(ids.length > 1 ? `${ids.length} files restored` : 'File restored');
        } catch (error) {
            unhideRows(ids.filter((id) => !restoredIds.includes(id)));
            // Earlier requests committed, so say how far the restore got before it stopped.
            errorMessage.show(
                error,
                restoredIds.length > 0
                    ? `${filesCount(restoredIds.length)} ${restoredIds.length === 1 ? 'was' : 'were'} restored before the error`
                    : undefined,
            );
        } finally {
            busyRef.current = false;
            setIsRestoring(false);
            if (restoredIds.length > 0) {
                setSelectedRowKeys((prev) => prev.filter((key) => !restoredIds.includes(key as string)));
                void refetch();
            }
        }
    };

    // Single type-to-confirm modal drives permanent-delete of a single row, the
    // bulk selection, and empty-trash. `ids === null` means empty trash.
    const { showConfirmationModal, contextHolder } = useDeleteConfirmation<{ ids: string[] | null; orgId: bigint }>({
        title: 'Permanently delete',
        content:
            'This permanently removes the file(s) and their stored data. This action cannot be undone. Type the confirmation text below to proceed.',
        okText: 'Permanently delete',
        entityDisplayName: 'file',
        onConfirm: async ({ params }) => {
            if (busyRef.current) {
                message.warning('Wait for the current restore or deletion to finish, then try again.');
                return;
            }
            // The modal retains its original selection across capability refreshes.
            // Reject the entire stale selection before optimistic removal or mutation.
            if (params.ids?.some((id) => readOnlyIds.has(id))) {
                setSelectedRowKeys([]);
                message.warning('File permissions have changed. Select the files again to permanently delete them.');
                return;
            }
            const idsToHide = params.ids ?? rows.filter((row) => row.canManageTrash).map((row) => row.id);
            hideRows(idsToHide);
            busyRef.current = true;
            setPurgeProgress({ deleted: 0, total: params.ids?.length ?? null });
            let deleted = 0;
            // Ids sent in a request, whether or not it succeeded; empty trash reports counts only.
            const submittedIds: string[] = [];
            try {
                if (params.ids === null) {
                    // Each call purges one bounded batch. Repeat until nothing purgeable
                    // remains, stopping if a call makes no progress so files the sweep
                    // cannot purge never loop forever. Later calls send back the first
                    // call's cutoff, so files trashed during the purge are not swept up.
                    let cutoff: Date | undefined;
                    for (;;) {
                        const result = await emptyTrashBatch({ orgId: params.orgId, cutoff });
                        cutoff = result.cutoff;
                        deleted += result.deleted;
                        setPurgeProgress({ deleted, total: deleted + result.remaining });
                        if (result.remaining === 0 || result.deleted === 0) break;
                    }
                } else {
                    // The selection persists across pages, so it can exceed the per-request cap.
                    for (const batch of chunk(params.ids, FILE_TRASH_BULK_LIMIT)) {
                        submittedIds.push(...batch);
                        deleted += (await permanentlyDeleteFiles({ ids: batch, orgId: params.orgId })).deleted;
                        setPurgeProgress({ deleted, total: params.ids.length });
                    }
                }
                message.success(`${filesCount(deleted)} permanently deleted`);
                setSelectedRowKeys([]);
                void refetch();
            } catch (error) {
                // Earlier requests committed, so say how far the purge got before it stopped.
                errorMessage.show(
                    error,
                    deleted > 0
                        ? `${filesCount(deleted)} ${deleted === 1 ? 'was' : 'were'} permanently deleted before the error`
                        : undefined,
                );
                // A rejected request does not prove a rollback: the server commits each chunk
                // separately, and a lost response can follow a full commit. Drop every id whose
                // outcome is unknown from the selection and keep the rows hidden until a fresh
                // list shows which remain (showing them again only if that read fails).
                setSelectedRowKeys((prev) =>
                    params.ids === null ? [] : prev.filter((key) => !submittedIds.includes(key as string)),
                );
                void refetch().then(({ isError }) => {
                    if (isError) unhideRows(idsToHide);
                });
            } finally {
                busyRef.current = false;
                setPurgeProgress(null);
            }
        },
    });

    const promptPermanentDeleteRow = (row: TrashRow) => showConfirmationModal(row.name, { ids: [row.id], orgId });
    const promptPermanentDeleteSelected = () =>
        showConfirmationModal('DELETE', { ids: writableSelectedRowKeys as string[], orgId });
    const promptEmptyTrash = () => showConfirmationModal('DELETE', { ids: null, orgId });

    const columns = useTrashTableColumns({
        onRestore: (id) => void handleRestore([id]),
        onPermanentDelete: promptPermanentDeleteRow,
        actionsDisabled: isBusy,
    });

    const tableProps = usePrismaTable({
        data: rows,
        total,
        loading: isFetching,
        columns,
        rowKey: 'id',
        queryParams: queryVariables,
        onParamsChange: setQueryVariables,
        pagination: { position: 'both', showSizeChanger: true, maxAccessibleRecords: DEEP_LIST_OFFSET_LIMIT },
        rowSelection: {
            enabled: true,
            type: 'checkbox',
            preserveSelectedRowKeys: true,
            selectedRowKeys: writableSelectedRowKeys,
            getCheckboxProps: (row) => ({ disabled: !row.canManageTrash || isBusy }),
            onChange(keys) {
                setSelectedRowKeys(keys.filter((key) => !readOnlyIds.has(String(key))));
            },
        },
        refreshable: { enabled: true, onRefresh: () => refetch() },
        emptyState: {
            noRecordsTitle: 'No deleted files',
            noRecordsDescription: 'Files you delete stay here until they are restored or permanently deleted.',
            // The filter bar feeds the query directly, not `queryParams.where`.
            hasActiveFilters: !isWhereClauseEmpty(filterWhere),
        },
    });

    return (
        <PageController route={Route}>
            {contextHolder}
            <TableToolbar
                variant="enhanced"
                filters={
                    <TrashFilters
                        onChange={(where) => {
                            setFilterWhere(where);
                            setQueryVariables((prev) => ({ ...prev, offset: 0 }));
                        }}
                    />
                }
                actions={
                    <Space size="small">
                        {writableSelectedRowKeys.length > 0 && (
                            <>
                                <span className="whitespace-nowrap text-muted-foreground text-sm">
                                    {writableSelectedRowKeys.length} selected
                                </span>
                                <Button
                                    size="small"
                                    loading={isRestoring}
                                    disabled={isBusy}
                                    onClick={() => void handleRestore(writableSelectedRowKeys as string[])}
                                >
                                    Restore
                                </Button>
                                <Button
                                    type="danger"
                                    size="small"
                                    icon={<Trash2 size={14} />}
                                    disabled={isBusy}
                                    onClick={promptPermanentDeleteSelected}
                                >
                                    Permanently delete
                                </Button>
                            </>
                        )}
                        <Button
                            type="danger"
                            size="small"
                            icon={<Trash2 size={14} />}
                            disabled={total === 0 || isBusy}
                            onClick={promptEmptyTrash}
                        >
                            Empty trash
                        </Button>
                    </Space>
                }
            />

            {purgeProgress && (
                <Callout variant="info" role="status" title="Permanently deleting files" className="mb-4">
                    <p>
                        {purgeProgress.total === null
                            ? 'Deleting the first batch of files.'
                            : `${purgeProgress.deleted} of ${filesCount(purgeProgress.total)} deleted.`}{' '}
                        Keep this tab open until it finishes.
                    </p>
                    <Progress
                        percent={
                            purgeProgress.total ? Math.round((purgeProgress.deleted / purgeProgress.total) * 100) : 0
                        }
                        status="active"
                        size="small"
                    />
                </Callout>
            )}

            <PrismaTable {...tableProps} />
        </PageController>
    );
}
